import axios from 'axios';

// Determine base URL from environment
const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Send cookies (refresh token)
  headers: {
    'Content-Type': 'application/json',
  },
});

// ========================
// Token Management
// ========================
let accessToken = null;
let refreshPromise = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export const clearAccessToken = () => {
  accessToken = null;
};

// ========================
// Request Interceptor
// ========================
api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ========================
// Response Interceptor (auto-refresh)
// ========================
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network error / offline detection
    if (!error.response && error.message === 'Network Error') {
      error.isOffline = true;
      error.response = { data: { message: 'You are offline. Please check your internet connection.', errorCode: 'OFFLINE' }, status: 0 };
      return Promise.reject(error);
    }

    // Tier / plan upgrade required — attach metadata for UI handling
    if (
      error.response?.status === 403 &&
      (error.response?.data?.error_code === 'PLAN_UPGRADE_REQUIRED' ||
       error.response?.data?.error_code === 'PLAN_LIMIT_REACHED')
    ) {
      error.isTierError = true;
      error.tierDetails = error.response.data.details || {};
      // Dispatch custom event so any listener (toasts, modals) can react
      window.dispatchEvent(new CustomEvent('projectrax:tier-blocked', {
        detail: {
          errorCode: error.response.data.error_code,
          message: error.response.data.message,
          ...error.response.data.details,
        },
      }));
      return Promise.reject(error);
    }

    // If 401 and not already retried, attempt token refresh
    if (
      error.response?.status === 401 &&
      error.response?.data?.error_code === 'TOKEN_EXPIRED' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        // Prevent multiple simultaneous refresh requests
        if (!refreshPromise) {
          refreshPromise = api.post('/auth/refresh').finally(() => {
            refreshPromise = null;
          });
        }

        const res = await refreshPromise;
        const newToken = res.data.data.accessToken;
        setAccessToken(newToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed — force logout
        clearAccessToken();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
